import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PageFilter } from '@common/paged-data/types/page-filter.type';
import { Page } from '@common/paged-data/types/page.type';
import { ApiService } from '@portal-core/auth/services/api.service';
import { LogPrepareResponse } from '@portal-core/live-log/models/log-prepare-response.model';
import { ProcessPublicationType } from '@portal-core/publications/enums/process-publication-type.enum';
import { Publication } from '@portal-core/publications/models/publication.model';
import { AccessMap, AccessRequest } from '@portal-core/data/types/access-request.type';
import { PublicationOperationType } from '@portal-core/publications/types/publication-operation-type.type';
import { SharePendingRequest } from '@portal-core/util/observables.decorators';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PublicationsApiService {
  constructor(private http: HttpClient, private apiService: ApiService) { }

  @SharePendingRequest()
  getPublication$(publicationId: number): Observable<Publication> {
    return this.http.get<Publication>(`${this.apiService.centralApiBaseUri}/api/Publications/${publicationId}`);
  }

  @SharePendingRequest()
  getAccess$(request: AccessRequest<Publication, PublicationOperationType>): Observable<AccessMap<PublicationOperationType>> {
    return this.http.post<AccessMap<PublicationOperationType>>(`${this.apiService.centralApiBaseUri}/api/Publications/Access`, request);
  }

  getPublicationLogStreamUrl(publicationId: number): string {
    return `${this.apiService.centralApiBaseUri}/api/Publications/${publicationId}/log/stream`;
  }

  preparePublicationLog$(publicationId: number): Observable<LogPrepareResponse> {
    return this.http.post<LogPrepareResponse>(`${this.apiService.centralApiBaseUri}/api/Publications/${publicationId}/log/prepare`, null);
  }

  @SharePendingRequest()
  getPublicationsByIds$(publicationIds: number[]): Observable<Publication[]> {
    return this.http.post<Publication[]>(`${this.apiService.centralApiBaseUri}/api/Publications/getBatchPublicationsByIds`, publicationIds);
  }

  @SharePendingRequest()
  getPublicationListByBuildId$(buildId: number): Observable<Publication[]> {
    return this.http.get<Publication[]>(`${this.apiService.centralApiBaseUri}/api/Publications/publicationListByBuildId/${buildId}`);
  }

  @SharePendingRequest()
  getPublicationsPage$(filterModel: PageFilter): Observable<Page<Publication>> {
    return this.http.post<Page<Publication>>(`${this.apiService.centralApiBaseUri}/api/Publications/AdminGrid`, filterModel);
  }

  @SharePendingRequest()
  getPublicationsPageByProjectId$(projectId: number, filterModel: PageFilter): Observable<Page<Publication>> {
    return this.http.post<Page<Publication>>(`${this.apiService.centralApiBaseUri}/api/Publications/PublicationsGrid/${projectId}`, filterModel);
  }

  @SharePendingRequest()
  createPublications$(buildId: number, destinationFilePaths: string[]): Observable<Publication[]> {
    return this.http.post<Publication[]>(`${this.apiService.centralApiBaseUri}/api/Publications/queuePublications`, {
      buildId,
      destinationFilePaths
    });
  }

  processPublication$(publicationId: number, processPublicationType: ProcessPublicationType): Observable<any> {
    const params = new HttpParams().set('processPublicationType', processPublicationType.toString());
    return this.http.post(`${this.apiService.centralApiBaseUri}/api/Publications/processPublication/${publicationId}`, null, { params });
  }

  bulkDeletePublications$(projectId: number, publicationIds: number[]): Observable<any> {
    return this.http.post(`${this.apiService.centralApiBaseUri}/api/Publications/bulkDeletePublications/${projectId}`, publicationIds);
  }
}
